import React from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonIcon,
  IonLoading,
  IonRefresher,
  IonRefresherContent,
  IonText,
  IonBadge,
} from "@ionic/react";
import {
  trophyOutline,
  calendarOutline,
  ribbonOutline,
  arrowForwardOutline,
} from "ionicons/icons";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import api from "@/services/api";
import { useI18n } from "@/i18n";

interface Season {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  total_rounds: number;
  completed_rounds: number;
  player_final_points: number | null;
  player_final_position: number | null;
  player_final_league: string | null;
  player_final_tier: number | null;
}

interface SeasonsResponse {
  seasons: Season[];
}

const History: React.FC = () => {
  const { t } = useI18n();
  const history = useHistory();

  const { data, isLoading, error, refetch } = useQuery<SeasonsResponse>(
    ["seasons-history"],
    async () => (await api.get("/api/leagues/seasons")).data
  );

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  const completedSeasons = data?.seasons.filter((s) => !s.is_active) || [];
  const activeSeason = data?.seasons.find((s) => s.is_active) || null;

  const getTierColor = (tier: number | null) => {
    if (tier === 1) return "var(--app-gold)";
    if (tier === 2) return "var(--app-silver)";
    return "var(--app-bronze)";
  };

  if (isLoading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t("historyTitle")}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonLoading isOpen message={t("loadingHistory")} />
        </IonContent>
      </IonPage>
    );
  }

  if (error) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t("historyTitle")}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText color="danger">
            <p>{t("errorLoadingHistory")}</p>
          </IonText>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("historyTitle")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div style={{ padding: "16px" }}>
          {/* Active season banner */}
          {activeSeason && (
            <IonCard style={{ margin: "0 0 16px 0" }}>
              <div
                style={{
                  padding: "16px",
                  background:
                    "linear-gradient(135deg, var(--ion-color-primary) 0%, var(--ion-color-primary-shade) 100%)",
                  color: "#fff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "6px",
                  }}
                >
                  <IonIcon icon={calendarOutline} style={{ fontSize: "20px" }} />
                  <strong style={{ fontSize: "16px" }}>
                    {activeSeason.name}
                  </strong>
                  <IonBadge
                    style={{
                      "--background": "var(--app-gold)",
                      color: "#000",
                      fontSize: "10px",
                    }}
                  >
                    {t("active")}
                  </IonBadge>
                </div>
                <p
                  style={{
                    margin: 0,
                    opacity: 0.85,
                    fontSize: "13px",
                  }}
                >
                  {t("roundLabel")} {activeSeason.completed_rounds + 1} /{" "}
                  {activeSeason.total_rounds}
                </p>
              </div>
            </IonCard>
          )}

          {/* Section heading */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
            }}
          >
            <IonIcon
              icon={trophyOutline}
              style={{ fontSize: "20px", color: "var(--app-gold)" }}
            />
            <strong style={{ fontSize: "16px" }}>
              {t("pastSeasons")}
            </strong>
          </div>

          {completedSeasons.length === 0 ? (
            <IonCard>
              <IonCardContent>
                <p style={{ color: "var(--ion-color-medium)", fontSize: "14px" }}>
                  {t("noPastSeasons")}
                </p>
              </IonCardContent>
            </IonCard>
          ) : (
            completedSeasons.map((season) => (
              <IonCard
                key={season.id}
                button
                onClick={() => history.push(`/history/${season.id}`)}
                style={{ margin: "0 0 12px 0", cursor: "pointer" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "16px",
                  }}
                >
                  {/* Season icon */}
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, var(--ion-color-primary) 0%, var(--ion-color-primary-shade) 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <IonIcon
                      icon={ribbonOutline}
                      style={{ fontSize: "22px", color: "#fff" }}
                    />
                  </div>

                  {/* Season info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: "16px", display: "block" }}>
                      {season.name}
                    </strong>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "var(--ion-color-medium)",
                      }}
                    >
                      {season.total_rounds} {t("roundsPlayed")}
                    </span>

                    {/* Player result */}
                    {season.player_final_points !== null && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          marginTop: "6px",
                        }}
                      >
                        <div
                          className="rank-badge"
                          style={{
                            width: "22px",
                            height: "22px",
                            fontSize: "11px",
                            background: getTierColor(season.player_final_tier),
                            color:
                              season.player_final_tier === 1 ? "#000" : "#fff",
                          }}
                        >
                          {season.player_final_position}
                        </div>
                        <span style={{ fontSize: "12px", fontWeight: 600 }}>
                          {season.player_final_points} {t("points")}
                        </span>
                        {season.player_final_league && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "var(--ion-color-medium)",
                            }}
                          >
                            · {season.player_final_league}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Arrow */}
                  <IonIcon
                    icon={arrowForwardOutline}
                    style={{
                      fontSize: "18px",
                      color: "var(--ion-color-medium)",
                      flexShrink: 0,
                    }}
                  />
                </div>
              </IonCard>
            ))
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default History;
